import React from 'react';
import {
  ArrowPathIcon,
  CircleStackIcon,
  CpuChipIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { FingerPrintIcon } from '@heroicons/react/24/solid';

const STATUS_META = {
  active: { label: 'Active', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  legacy_partial: { label: 'Partial legacy', dot: 'bg-amber-500', text: 'text-amber-700' },
  legacy_not_used_for_identity: { label: 'Legacy', dot: 'bg-gray-400', text: 'text-gray-500' },
  inactive: { label: 'Inactive', dot: 'bg-gray-400', text: 'text-gray-500' },
};

const statusMeta = (status) =>
  STATUS_META[status] || { label: status ? String(status).replace(/_/g, ' ') : '—', dot: 'bg-gray-300', text: 'text-gray-500' };

const formatDate = (dateString) => {
  if (!dateString) return '';
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return dateString;
  return parsed.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatScopeLine = (scope) => {
  if (!Array.isArray(scope) || scope.length === 0) return null;
  const set = new Set(scope.map((s) => String(s).toLowerCase()));
  const parts = [];
  if (set.has('read')) parts.push('read');
  if (set.has('write')) parts.push('write');
  if (set.has('delete')) parts.push('delete');
  const extras = [...set].filter((s) => !['read', 'write', 'delete'].includes(s));
  if (extras.length) parts.push(...extras.slice(0, 2));
  return parts.join(' · ');
};

const fileBasename = (path) => {
  if (!path) return '';
  const parts = path.split('/');
  return parts[parts.length - 1] || path;
};

const fileFolder = (path) => {
  if (!path || !path.includes('/')) return '';
  return path.slice(0, path.lastIndexOf('/'));
};

const StatusDot = ({ status }) => {
  const meta = statusMeta(status);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${meta.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const SectionLabel = ({ children, count }) => (
  <div className="flex items-baseline gap-2 mb-3 px-0.5">
    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{children}</h2>
    {typeof count === 'number' && (
      <span className="text-xs text-gray-300 tabular-nums">{count}</span>
    )}
  </div>
);

const EngineCard = ({ engine }) => {
  const tools = Array.isArray(engine.tools_summary) ? engine.tools_summary : [];
  const visibleTools = tools.slice(0, 3);
  const extra = tools.length - visibleTools.length;
  const showLegacyBadge = engine.identity_migrated_to_mcpcustom === false;

  return (
    <div className="rounded-xl border border-gray-200/80 bg-white p-4 hover:border-module-identity/30 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900 truncate">
            {engine.name || engine.id}
          </p>
          {engine.init_rule && (
            <p className="text-xs text-gray-400 mt-0.5 truncate" title={engine.init_rule}>
              {engine.init_rule}
            </p>
          )}
        </div>
        <StatusDot status={engine.status} />
      </div>

      {visibleTools.length > 0 && (
        <ul className="mt-3 space-y-1">
          {visibleTools.map((tool) => (
            <li key={tool} className="text-xs text-gray-600 truncate" title={tool}>
              {tool}
            </li>
          ))}
          {extra > 0 && (
            <li className="text-xs text-gray-400">+{extra} tools</li>
          )}
        </ul>
      )}

      {showLegacyBadge && (
        <p className="mt-3 text-[11px] font-medium text-amber-700 bg-amber-50 inline-flex px-2 py-0.5 rounded">
          Identity still on legacy
        </p>
      )}
    </div>
  );
};

const DataSourceRow = ({ source }) => {
  const scopeLine = formatScopeLine(source.scope);
  const perEngine = source.status_by_engine;
  const detail = Array.isArray(source.modules) && source.modules.length
    ? source.modules.join(', ')
    : Array.isArray(source.repos) && source.repos.length
      ? source.repos.join(', ')
      : null;

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <CircleStackIcon className="h-4 w-4 text-gray-300 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-gray-800 truncate">
            {source.label || source.id}
          </p>
          {!perEngine && <StatusDot status={source.status} />}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-400">
          {scopeLine && <span>{scopeLine}</span>}
          {detail && (
            <>
              {scopeLine && <span className="text-gray-200">·</span>}
              <span className="truncate">{detail}</span>
            </>
          )}
        </div>
        {perEngine && (
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
            {Object.entries(perEngine).map(([engine, status]) => (
              <span key={engine} className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <span className="capitalize">{engine}</span>
                <StatusDot status={status} />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const OverviewSkeleton = () => (
  <div className="pb-6" aria-busy="true" aria-label="Loading">
    <div className="px-4 pt-4 pb-5">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-gray-200" />
        <div className="flex-1 space-y-2 pt-1">
          <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-56 animate-pulse rounded bg-gray-100" />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <div className="h-7 w-28 animate-pulse rounded-lg bg-gray-100" />
        <div className="h-7 w-20 animate-pulse rounded-lg bg-gray-100" />
      </div>
    </div>
    <div className="space-y-7 px-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="h-28 animate-pulse rounded-xl bg-gray-100" />
        <div className="h-28 animate-pulse rounded-xl bg-gray-100" />
      </div>
      <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
    </div>
  </div>
);

const MainOverview = ({ instanceState, instanceStateLoading, files, isLoading, onFileClick, onRefresh }) => {
  const recentFiles = [...files]
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 5);

  const hasParseErrors = instanceState?.parse_errors && Object.keys(instanceState.parse_errors).length > 0;

  if (instanceStateLoading) {
    return <OverviewSkeleton />;
  }

  if (!instanceState) {
    return (
      <div className="text-center py-20 px-6 text-gray-400">
        <FingerPrintIcon className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm text-gray-600">Overview unavailable</p>
        <p className="text-xs mt-1.5 max-w-xs mx-auto leading-relaxed">
          Could not read STATE.md. Check the instance root in Settings, or whether the file exists in Files.
        </p>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
            Try again
          </button>
        )}
      </div>
    );
  }

  const {
    data_sources: dataSources = [],
    engines = [],
    version,
    updated_at: updatedAt,
    instance_root: instanceRoot,
    state_path: statePath,
  } = instanceState;

  const activeEngines = engines.filter((e) => e.status === 'active').length;
  const identityOk = engines.some((e) => e.identity_migrated_to_mcpcustom === true);
  const displayName = instanceRoot || 'Identity';

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="px-4 pt-4 pb-5 bg-gradient-to-b from-module-identity/10 to-transparent">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white border border-module-identity/20 flex items-center justify-center flex-shrink-0">
            <FingerPrintIcon className="h-5 w-5 text-module-identity" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold text-gray-900 truncate">{displayName}</p>
            <p className="text-xs text-gray-400 mt-0.5 truncate" title={statePath}>
              {statePath || 'STATE.md'}
              {version != null && version !== '' && (
                <span className="text-gray-300"> · v{version}</span>
              )}
            </p>
          </div>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-module-identity hover:bg-white/80 transition-colors"
              title="Sync with STATE.md"
            >
              <ArrowPathIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/80 border border-gray-100 text-xs text-gray-600">
            <CpuChipIcon className="h-3.5 w-3.5 text-module-identity" />
            {activeEngines}/{engines.length || 0} active engines
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/80 border border-gray-100 text-xs text-gray-600">
            <CircleStackIcon className="h-3.5 w-3.5 text-module-identity" />
            {dataSources.length} sources
          </span>
          {identityOk && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-100 text-xs text-emerald-700">
              Identity via MCP
            </span>
          )}
          {updatedAt && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-white/80 border border-gray-100 text-xs text-gray-400">
              STATE {formatDate(updatedAt)}
            </span>
          )}
        </div>
      </div>

      <div className="px-4 space-y-7 mt-1">
        {hasParseErrors && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800">
              <p className="font-medium mb-0.5">Some STATE.md blocks could not be read</p>
              {Object.entries(instanceState.parse_errors).map(([key, msg]) => (
                <p key={key} className="text-amber-700/90">{key}: {msg}</p>
              ))}
            </div>
          </div>
        )}

        {/* Engines first */}
        <section>
          <SectionLabel count={engines.length}>Engines</SectionLabel>
          {engines.length === 0 ? (
            <p className="text-xs text-gray-400 px-0.5">No engines registered.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {engines.map((engine) => (
                <EngineCard key={engine.id || engine.name} engine={engine} />
              ))}
            </div>
          )}
        </section>

        {/* Sources compact */}
        <section>
          <SectionLabel count={dataSources.length}>Data sources</SectionLabel>
          {dataSources.length === 0 ? (
            <p className="text-xs text-gray-400 px-0.5">No sources registered.</p>
          ) : (
            <div className="rounded-xl border border-gray-200/80 bg-white px-3">
              {dataSources.map((source) => (
                <DataSourceRow key={source.id || source.label} source={source} />
              ))}
            </div>
          )}
        </section>

        {/* Recent files */}
        {!isLoading && recentFiles.length > 0 && (
          <section>
            <SectionLabel>Recently updated</SectionLabel>
            <div className="rounded-xl border border-gray-200/80 bg-white divide-y divide-gray-50">
              {recentFiles.map((file) => {
                const folder = fileFolder(file.path);
                return (
                  <button
                    key={file.path}
                    type="button"
                    onClick={() => onFileClick(file)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-module-identity/10 transition-colors text-left"
                  >
                    <DocumentTextIcon className="h-4 w-4 text-gray-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{fileBasename(file.path)}</p>
                      {folder && (
                        <p className="text-[11px] text-gray-400 truncate">{folder}</p>
                      )}
                    </div>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                      {formatDate(file.updated_at)}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default MainOverview;
